"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";

type SettingsNavigationContextValue = {
  openSettings: () => void;
};

const SETTINGS_HASH = "settings";

const SettingsNavigationContext = createContext<SettingsNavigationContextValue>({
  openSettings: () => {},
});

function clearHash() {
  if (typeof window === "undefined" || !window.location.hash) return;

  // history.replaceState rather than router.replace: the app router treats a
  // replace to the current route as a no-op and leaves the hash in place, which
  // makes every subsequent click on a #settings link a no-op too.
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, "", `${pathname}${search}`);
}

export function SettingsNavigationProvider({ children }: { children: ReactNode }) {
  const router = useRouter();

  const openSettings = useCallback(() => router.push("/settings"), [router]);

  // Support #settings links (including ones followed from another page).
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace("#", "").split("?")[0];
      if (hash !== SETTINGS_HASH) return;

      clearHash();
      router.push("/settings");
    };

    handleHashChange();

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [router]);

  const value = useMemo(
    () => ({ openSettings }),
    [openSettings],
  );

  return (
    <SettingsNavigationContext.Provider value={value}>
      {children}
    </SettingsNavigationContext.Provider>
  );
}

export function useSettingsNavigation() {
  return useContext(SettingsNavigationContext);
}
